import { useMemo } from "react";
import { useFetcher } from "~/hooks/useFetcher";
import { type AgentList, AgentScope, agentListPath } from "~/services/agents";

/**
 * Resolves the UUID of the SYSTEM agent with the given `name` (e.g.
 * "dataset-assistant") by scanning `GET /agents`.
 *
 * The dataset assistant resolves this up front and threads it into
 * createConversation, so new conversations persist `agent_id` rather than
 * relying on the orchestrator mapping `app_id` to an agent at turn time (the
 * fallback while the agent list is still loading). The resolved id also tells
 * the connector picker whose granted connectors to fetch.
 *
 * Returns null while the list is loading and when no SYSTEM agent matches.
 */
export function useSystemAgentId(name: string | null): string | null {
  const { data } = useFetcher<AgentList>(name ? agentListPath() : null);

  return useMemo(() => {
    if (!name || !data) {
      return null;
    }
    const match = data.items.find(
      (agent) => agent.name === name && agent.scope === AgentScope.SYSTEM
    );
    return match?.id ?? null;
  }, [data, name]);
}
